import { useState } from "react";
import { useAdminAuditLog, AuditLogEntry } from "@/hooks/queries/useAdminAuditLog";

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function JsonCell({ value }: { value: Record<string, unknown> | null }) {
  const [expanded, setExpanded] = useState(false);
  if (!value) return <span className="text-gray-300">—</span>;
  const preview = JSON.stringify(value).slice(0, 60);
  const full = JSON.stringify(value, null, 2);
  return (
    <div>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="font-mono text-xs text-blue-600 hover:underline text-left"
        title={full}
      >
        {expanded ? "▲ hide" : preview.length < 60 ? preview : preview + "…"}
      </button>
      {expanded && (
        <pre className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded text-xs font-mono whitespace-pre-wrap text-gray-700 max-w-xs overflow-auto max-h-40">
          {full}
        </pre>
      )}
    </div>
  );
}

function RoleChip({ role }: { role: string | null }) {
  const colours: Record<string, string> = {
    founder: "bg-purple-100 text-purple-700",
    ops: "bg-blue-100 text-blue-700",
    support: "bg-cyan-100 text-cyan-700",
    space_owner: "bg-amber-100 text-amber-700",
  };
  const cls = colours[role ?? ""] ?? "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {role ?? "unknown"}
    </span>
  );
}

const TABLE_OPTIONS = [
  "", "acorn_rules", "ai_prompts", "ai_model_pricing", "ai_debug_accounts",
  "content_library", "shop_items", "shop_promo_code_pools",
  "email_shells", "email_template_variants", "subscriptions",
  "children",
];

export default function AuditLog() {
  const [actionFilter, setActionFilter] = useState("");
  const [tableFilter, setTableFilter] = useState("");
  const [limitFilter, setLimitFilter] = useState(200);

  const { data: entries = [], isLoading, isError, error, refetch } =
    useAdminAuditLog({
      action: actionFilter || undefined,
      target_table: tableFilter || undefined,
      limit: limitFilter,
    });

  return (
    <div className="p-8 max-w-screen-xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Durable record of all admin mutations. Founder-only read. Write-once — no edits.
          </p>
        </div>
        <button
          onClick={() => void refetch()}
          className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Action (contains)</label>
          <input
            type="text"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="e.g. tier.grant"
            className="rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400 w-44"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Table</label>
          <select
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            className="rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
          >
            {TABLE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t || "— all tables —"}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Rows</label>
          <select
            value={limitFilter}
            onChange={(e) => setLimitFilter(Number(e.target.value))}
            className="rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
          >
            {[50, 100, 200, 500].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-gray-500">Loading…</p>
      )}
      {isError && (
        <p className="text-sm text-red-600">
          Failed to load: {(error as Error)?.message ?? "unknown error"}
        </p>
      )}
      {!isLoading && !isError && entries.length === 0 && (
        <p className="text-sm text-gray-400">No log entries match the current filters.</p>
      )}

      {entries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th className="pb-2 pr-4 whitespace-nowrap">Timestamp</th>
                <th className="pb-2 pr-4">Actor</th>
                <th className="pb-2 pr-4">Action</th>
                <th className="pb-2 pr-4">Table</th>
                <th className="pb-2 pr-4">Target ID</th>
                <th className="pb-2 pr-4">Before</th>
                <th className="pb-2">After</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((row: AuditLogEntry) => (
                <tr key={row.id} className="hover:bg-gray-50 align-top">
                  <td className="py-2.5 pr-4 text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="py-2.5 pr-4">
                    <RoleChip role={row.actor_role} />
                    <p className="font-mono text-xs text-gray-400 mt-0.5 max-w-[140px] truncate" title={row.actor_id}>
                      {row.actor_id.slice(0, 8)}…
                    </p>
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className="font-mono text-xs text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded">
                      {row.action}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-gray-600 whitespace-nowrap">
                    {row.target_table ?? "—"}
                  </td>
                  <td className="py-2.5 pr-4 font-mono text-xs text-gray-500 max-w-[120px]">
                    {row.target_id ? (
                      <span title={row.target_id} className="truncate block">
                        {row.target_id.length > 16 ? row.target_id.slice(0, 8) + "…" : row.target_id}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-2.5 pr-4">
                    <JsonCell value={row.before} />
                  </td>
                  <td className="py-2.5">
                    <JsonCell value={row.after} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-400">
            Showing {entries.length} of last {limitFilter} matching rows, newest first.
          </p>
        </div>
      )}
    </div>
  );
}
